import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Calendar, Clock, User, Download, Filter } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const NewsletterSection = ({ subscribers, isMobile }) => {
    const now = new Date();
    const thisMonthSubscribers = subscribers.filter(s => {
        const d = s.timestamp instanceof Date ? s.timestamp : (s.timestamp?.toDate ? s.timestamp.toDate() : null);
        if (!d) return false;
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { label: 'Total Subscribers', value: subscribers.length, color: 'text-violet-500', bg: 'bg-violet-500/10' },
                    { label: 'Joining This Month', value: thisMonthSubscribers, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
                ].map((stat, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                        <Card className="p-8 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2rem] hover:scale-105 transition-transform">
                            <div className={`text-4xl font-black ${stat.color} tracking-tighter mb-2`}>{stat.value}</div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">{stat.label}</div>
                        </Card>
                    </motion.div>
                ))}
            </div>

            <Card className="bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] overflow-hidden shadow-2xl">
                <div className="p-8 border-bottom border-border/40 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner">
                            <Mail size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-foreground tracking-tight">Newsletter Database</h3>
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60">Complete list of registered subscribers</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Button variant="outline" className="rounded-xl font-black uppercase tracking-widest text-[10px] gap-2 border-border/60">
                            <Download size={14} /> Export CSV
                        </Button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-secondary/20 border-y border-border/40">
                                {['#', 'Subscriber Details', 'Subscription Date', 'Status'].map(h => (
                                    <th key={h} className="px-8 py-5 text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-70">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/40">
                            {subscribers.map((s, i) => {
                                const raw = s.timestamp instanceof Date ? s.timestamp : (s.timestamp?.toDate ? s.timestamp.toDate() : null);
                                const dateStr = raw ? raw.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                                const timeStr = raw ? raw.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
                                return (
                                    <motion.tr
                                        key={s.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: i * 0.02 }}
                                        className="group hover:bg-primary/[0.02] transition-colors"
                                    >
                                        <td className="px-8 py-6 text-xs font-black text-muted-foreground/60">{i + 1}</td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-black text-sm border border-primary/20 group-hover:scale-110 transition-transform">
                                                    {(s.email || '?')[0].toUpperCase()}
                                                </div>
                                                <div className="font-bold text-foreground tracking-tight">{s.email}</div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
                                                    <Calendar size={14} className="text-primary/60" /> {dateStr}
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] font-black text-muted-foreground/40 uppercase tracking-widest">
                                                    <Clock size={12} /> {timeStr}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 rounded-lg px-3 py-1 font-black text-[10px] uppercase tracking-widest">Active</Badge>
                                        </td>
                                    </motion.tr>
                                );
                            })}
                            {subscribers.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="p-20 text-center text-muted-foreground/40 italic font-bold">
                                        <Mail size={48} className="mx-auto mb-4 opacity-10" />
                                        No subscribers found in database.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </motion.div>
    );
};

export default NewsletterSection;
